import { useEffect, useRef, useCallback, useState, forwardRef } from 'react';
import { motion } from 'framer-motion';

// AIBL Logo
const AIBL_LOGO = 'https://customer-assets.emergentagent.com/job_aibl-orbit-fix/artifacts/alowj3rh_Group%2083.png';

// User-provided logos
const NANOBOT_LOGO = 'https://customer-assets.emergentagent.com/job_aibl-finance/artifacts/jwyvtazz_nanobot_logo%20%281%29.png';
const OPENCLAW_LOGO = 'https://customer-assets.emergentagent.com/job_aibl-finance/artifacts/awoldcvg_orbYx-3U_400x400.jpg';

// Framework data - positions as percentages relative to container center
const frameworks = [
  // LARGE TIER - closer to center, balanced
  { id: 'langchain', name: 'LangChain', logo: 'https://cdn.simpleicons.org/langchain/white', x: -20, y: -30, size: 'large', delay: 0, floatOffset: 0 },
  { id: 'openai', name: 'OpenAI', logo: 'https://avatars.githubusercontent.com/openai?s=128', x: 20, y: -30, size: 'large', delay: 0.1, floatOffset: 1 },
  { id: 'crewai', name: 'CrewAI', logo: 'https://avatars.githubusercontent.com/crewAIInc?s=128', x: -35, y: 0, size: 'large', delay: 0.2, floatOffset: 2 },
  { id: 'claude', name: 'Claude', logo: 'https://cdn.simpleicons.org/anthropic/white', x: 35, y: 0, size: 'large', delay: 0.3, floatOffset: 3 },
  { id: 'gemini', name: 'Gemini', logo: 'https://cdn.simpleicons.org/googlegemini/white', x: 0, y: 35, size: 'large', delay: 0.4, floatOffset: 4 },
  
  // MEDIUM TIER - ring around center
  { id: 'autogpt', name: 'AutoGPT', logo: 'https://avatars.githubusercontent.com/Significant-Gravitas?s=128', x: -10, y: -18, size: 'medium', delay: 0.5, floatOffset: 5 },
  { id: 'nanobot', name: 'Nanobot', logo: NANOBOT_LOGO, x: 10, y: -18, size: 'medium', delay: 0.55, floatOffset: 6 },
  { id: 'llamaindex', name: 'LlamaIndex', logo: 'https://avatars.githubusercontent.com/run-llama?s=128', x: -28, y: -15, size: 'medium', delay: 0.6, floatOffset: 7 },
  { id: 'n8n', name: 'n8n', logo: 'https://cdn.simpleicons.org/n8n/white', x: 28, y: -15, size: 'medium', delay: 0.65, floatOffset: 8 },
  { id: 'huggingface', name: 'HuggingFace', logo: 'https://cdn.simpleicons.org/huggingface/white', x: -28, y: 15, size: 'medium', delay: 0.7, floatOffset: 9 },
  { id: 'phidata', name: 'Phidata', logo: 'https://avatars.githubusercontent.com/phidatahq?s=128', x: 28, y: 15, size: 'medium', delay: 0.75, floatOffset: 10 },
  { id: 'autogen', name: 'AutoGen', logo: 'https://avatars.githubusercontent.com/microsoft?s=128', x: -10, y: 20, size: 'medium', delay: 0.8, floatOffset: 11 },
  { id: 'mistral', name: 'Mistral', logo: 'https://avatars.githubusercontent.com/mistralai?s=128', x: 10, y: 20, size: 'medium', delay: 0.85, floatOffset: 12 },
  
  // SMALL TIER - outer ring
  { id: 'eliza', name: 'Eliza', logo: 'https://avatars.githubusercontent.com/elizaOS?s=128', x: -40, y: -25, size: 'small', delay: 0.9, floatOffset: 13 },
  { id: 'vercel', name: 'Vercel AI', logo: 'https://avatars.githubusercontent.com/vercel?s=128', x: 40, y: -25, size: 'small', delay: 0.95, floatOffset: 14 },
  { id: 'openclaw', name: 'Openclaw', logo: OPENCLAW_LOGO, x: -42, y: 5, size: 'small', delay: 1.0, floatOffset: 15 },
  { id: 'e2b', name: 'E2B', logo: 'https://avatars.githubusercontent.com/e2b-dev?s=128', x: 42, y: 5, size: 'small', delay: 1.05, floatOffset: 16 },
  { id: 'groq', name: 'Groq', logo: 'https://avatars.githubusercontent.com/groq?s=128', x: -38, y: 25, size: 'small', delay: 1.1, floatOffset: 17 },
  { id: 'livekit', name: 'LiveKit', logo: 'https://avatars.githubusercontent.com/livekit?s=128', x: 38, y: 25, size: 'small', delay: 1.15, floatOffset: 18 },
  { id: 'griptape', name: 'Griptape', logo: 'https://avatars.githubusercontent.com/griptape-ai?s=128', x: -15, y: 38, size: 'small', delay: 1.2, floatOffset: 19 },
];

// Size configurations
const sizeConfig = {
  large: { circle: 56, logo: 32, labelOpacity: 0.7 },
  medium: { circle: 44, logo: 24, labelOpacity: 0.5 },
  small: { circle: 34, logo: 18, labelOpacity: 0.35 },
};

// Pure CSS Floating Icon - no JS animation, no random values causing re-renders
const FloatingIcon = forwardRef(({ framework, isVisible }, ref) => {
  const config = sizeConfig[framework.size];
  
  // Use deterministic values based on floatOffset to avoid jitter from random()
  const floatVariant = (framework.floatOffset % 4) + 1; // 1-4 variants
  const floatDelay = (framework.floatOffset * 0.5) % 10; // Stagger the float starts
  
  return (
    <div
      ref={ref}
      className={`framework-icon framework-float-${floatVariant} absolute flex flex-col items-center gap-2`}
      style={{
        left: `calc(50% + ${framework.x}%)`,
        top: `calc(50% + ${framework.y}%)`,
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
        opacity: isVisible ? 1 : 0,
        animationDelay: isVisible ? `${floatDelay}s` : '0s',
        transitionDelay: `${framework.delay}s`,
      }}
      data-testid={`framework-${framework.id}`}
    >
      <div 
        className="icon-circle flex items-center justify-center rounded-full"
        style={{
          width: `${config.circle}px`,
          height: `${config.circle}px`,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <img 
          src={framework.logo} 
          alt={framework.name}
          className="object-contain rounded-md"
          style={{
            width: `${config.logo}px`,
            height: `${config.logo}px`,
            filter: 'brightness(0.9)',
          }}
          loading="lazy"
        />
      </div>
      <span 
        className="text-center whitespace-nowrap font-normal"
        style={{
          fontSize: framework.size === 'large' ? '13px' : framework.size === 'medium' ? '12px' : '11px',
          color: `rgba(255,255,255,${config.labelOpacity})`,
        }}
      >
        {framework.name}
      </span>
    </div>
  );
});

// Glossy AIBL Logo with soft, layered glow (no harsh edges)
function AIBLLogo({ centerRef }) {
  return (
    <div
      ref={centerRef}
      className="aibl-logo-container relative z-10"
      data-testid="aibl-center"
    >
      {/* Soft ambient glow - base layer */}
      <div 
        className="absolute rounded-full pointer-events-none"
        style={{
          width: '220px',
          height: '220px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(0, 82, 255, 0.08) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />

      {/* Concentric rings - gentle pulse with very subtle opacity */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="ring-animate ring-outer absolute rounded-full" />
        <div className="ring-animate ring-middle absolute rounded-full" />
        <div className="ring-animate ring-inner absolute rounded-full" />
      </div>

      {/* Main glossy orb */}
      <div className="glossy-orb">
        {/* Inner glass sphere */}
        <div className="orb-inner">
          {/* Top highlight - soft crescent */}
          <div 
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 60% 30% at 50% 20%, rgba(255,255,255,0.08), transparent)',
            }}
          />
          
          {/* Subtle glass shine */}
          <div 
            className="absolute rounded-full pointer-events-none overflow-hidden"
            style={{
              top: '8%',
              left: '20%',
              right: '20%',
              height: '15%',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)',
              borderRadius: '50% 50% 50% 50% / 100% 100% 0% 0%',
            }}
          />

          {/* AIBL Logo */}
          <img 
            src={AIBL_LOGO} 
            alt="AIBL"
            className="w-[90px] h-[90px] object-contain relative z-10"
            style={{ filter: 'drop-shadow(0 0 8px rgba(0, 82, 255, 0.2))' }}
          />
        </div>

        {/* Rotating edge glow - very subtle */}
        <div className="orb-edge-glow" />
      </div>

      {/* Pulse glow overlay (for line arrivals) */}
      <div 
        className="aibl-pulse-glow absolute pointer-events-none rounded-full"
        style={{
          width: '280px',
          height: '280px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(0, 82, 255, 0.3) 0%, rgba(0, 82, 255, 0.1) 30%, transparent 60%)',
          opacity: 0,
          transition: 'opacity 0.2s ease-out',
          zIndex: 5,
        }}
      />
    </div>
  );
}

// Pulse Lines Manager Class
class PulseLineManager {
  constructor(svg, centerEl, iconEls, container) {
    this.svg = svg;
    this.center = centerEl;
    this.icons = iconEls;
    this.container = container;
    this.activeLines = 0;
    this.maxActive = 3;
    this.interval = null;
    this.running = false;
  }

  getCenter(el) {
    const rect = el.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2 - containerRect.left,
      y: rect.top + rect.height / 2 - containerRect.top,
    };
  }

  fireLine() {
    if (this.activeLines >= this.maxActive || !this.running) return;

    const icon = this.icons[Math.floor(Math.random() * this.icons.length)];
    if (!icon) return;

    const from = this.getCenter(icon);
    const to = this.getCenter(this.center);

    this.activeLines++;

    // Brighten the sending icon
    const iconCircle = icon.querySelector('.icon-circle');
    if (iconCircle) {
      iconCircle.style.borderColor = 'rgba(255,255,255,0.25)';
      iconCircle.style.boxShadow = '0 0 15px rgba(255,255,255,0.08)';
      setTimeout(() => {
        iconCircle.style.borderColor = '';
        iconCircle.style.boxShadow = '';
      }, 800);
    }

    // Create SVG line
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', from.x);
    line.setAttribute('y1', from.y);
    line.setAttribute('x2', to.x);
    line.setAttribute('y2', to.y);
    line.setAttribute('stroke', 'rgba(255,255,255,0.15)');
    line.setAttribute('stroke-width', '1');
    line.style.filter = 'drop-shadow(0 0 2px rgba(255,255,255,0.1))';
    line.style.opacity = '0';
    line.style.transition = 'opacity 0.2s ease';
    this.svg.appendChild(line);

    requestAnimationFrame(() => {
      line.style.opacity = '1';
    });

    // Create traveling dot with trail
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('r', '3');
    dot.setAttribute('cx', from.x);
    dot.setAttribute('cy', from.y);
    dot.setAttribute('fill', 'white');
    dot.setAttribute('opacity', '0.8');
    dot.style.filter = 'drop-shadow(0 0 6px rgba(255,255,255,0.5))';
    this.svg.appendChild(dot);

    const trail1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    trail1.setAttribute('r', '2');
    trail1.setAttribute('fill', 'white');
    trail1.setAttribute('opacity', '0.3');
    this.svg.appendChild(trail1);

    const trail2 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    trail2.setAttribute('r', '1.5');
    trail2.setAttribute('fill', 'white');
    trail2.setAttribute('opacity', '0.1');
    this.svg.appendChild(trail2);

    const duration = 1000;
    const start = performance.now();

    const animate = (now) => {
      if (!this.running) {
        line.remove();
        dot.remove();
        trail1.remove();
        trail2.remove();
        this.activeLines--;
        return;
      }

      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * progress;

      const currentX = from.x + (to.x - from.x) * eased;
      const currentY = from.y + (to.y - from.y) * eased;

      dot.setAttribute('cx', currentX);
      dot.setAttribute('cy', currentY);

      const t1 = Math.max(0, eased - 0.08);
      const t2 = Math.max(0, eased - 0.16);
      trail1.setAttribute('cx', from.x + (to.x - from.x) * t1);
      trail1.setAttribute('cy', from.y + (to.y - from.y) * t1);
      trail2.setAttribute('cx', from.x + (to.x - from.x) * t2);
      trail2.setAttribute('cy', from.y + (to.y - from.y) * t2);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.pulseCenter();
        
        dot.style.transition = 'opacity 0.3s';
        dot.style.opacity = '0';
        trail1.style.transition = 'opacity 0.3s';
        trail1.style.opacity = '0';
        trail2.style.transition = 'opacity 0.3s';
        trail2.style.opacity = '0';

        setTimeout(() => {
          line.style.transition = 'opacity 0.6s';
          line.style.opacity = '0';
        }, 400);

        setTimeout(() => {
          line.remove();
          dot.remove();
          trail1.remove();
          trail2.remove();
          this.activeLines--;
        }, 1000);
      }
    };

    requestAnimationFrame(animate);
  }

  pulseCenter() {
    if (!this.center) return;
    const glowEl = this.center.querySelector('.aibl-pulse-glow');
    if (glowEl) {
      glowEl.style.opacity = '1';
      setTimeout(() => {
        glowEl.style.opacity = '0';
      }, 300);
    }
  }

  start() {
    this.running = true;
    this.fireLine();
    const scheduleNext = () => {
      if (!this.running) return;
      const delay = 1200 + Math.random() * 800;
      this.interval = setTimeout(() => {
        this.fireLine();
        scheduleNext();
      }, delay);
    };
    scheduleNext();
  }

  stop() {
    this.running = false;
    clearTimeout(this.interval);
  }
}

export default function WorksWithConstellation() {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const centerRef = useRef(null);
  const iconRefs = useRef([]);
  const pulseManagerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Trigger staggered entry
            setIsVisible(true);
            
            // Start pulse lines after icons settle
            if (!pulseManagerRef.current) {
              setTimeout(() => {
                const svg = svgRef.current;
                const center = centerRef.current;
                const icons = iconRefs.current.filter(Boolean);

                if (svg && center && icons.length > 0) {
                  pulseManagerRef.current = new PulseLineManager(svg, center, icons, container);
                  pulseManagerRef.current.start();
                }
              }, 2000); // Wait for icons to settle
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (pulseManagerRef.current) {
        pulseManagerRef.current.stop();
      }
    };
  }, [isMobile]);

  const setIconRef = useCallback((index) => (el) => {
    iconRefs.current[index] = el;
  }, []);

  return (
    <section 
      id="works-with" 
      className="relative bg-black overflow-hidden"
      data-testid="works-with-section"
      style={{ minHeight: '100vh' }}
    >
      {/* CSS Animations - Pure CSS, no JS dependencies */}
      <style>{`
        /* ===========================================
           FLOATING ICON ANIMATIONS
           4 variants to create organic variety without JS random()
        =========================================== */
        
        .framework-icon {
          will-change: transform, opacity;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform-style: preserve-3d;
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        /* Variant 1: Gentle up-down */
        .framework-float-1 {
          animation: floatV1 10s ease-in-out infinite;
        }
        @keyframes floatV1 {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-6px); }
        }
        
        /* Variant 2: Slight diagonal */
        .framework-float-2 {
          animation: floatV2 12s ease-in-out infinite;
        }
        @keyframes floatV2 {
          0%, 100% { transform: translate(-50%, -50%) translate(0, 0); }
          33% { transform: translate(-50%, -50%) translate(4px, -3px); }
          66% { transform: translate(-50%, -50%) translate(-2px, 2px); }
        }
        
        /* Variant 3: Gentle sway */
        .framework-float-3 {
          animation: floatV3 11s ease-in-out infinite;
        }
        @keyframes floatV3 {
          0%, 100% { transform: translate(-50%, -50%) translateX(0); }
          50% { transform: translate(-50%, -50%) translateX(5px); }
        }
        
        /* Variant 4: Subtle bounce */
        .framework-float-4 {
          animation: floatV4 9s ease-in-out infinite;
        }
        @keyframes floatV4 {
          0%, 100% { transform: translate(-50%, -50%) translate(0, 0); }
          25% { transform: translate(-50%, -50%) translate(-3px, -4px); }
          75% { transform: translate(-50%, -50%) translate(3px, 2px); }
        }
        
        /* Icon hover effects */
        .framework-icon .icon-circle {
          transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .framework-icon:hover .icon-circle {
          background: rgba(255,255,255,0.08) !important;
          border-color: rgba(255,255,255,0.15) !important;
          box-shadow: 0 0 20px rgba(255,255,255,0.05);
        }
        .framework-icon span {
          transition: color 0.3s ease;
        }
        .framework-icon:hover span {
          color: rgba(255,255,255,0.8) !important;
        }
        
        /* ===========================================
           GLOSSY ORB STYLES
           Soft, layered glow without harsh edges
        =========================================== */
        
        .glossy-orb {
          position: relative;
          width: 150px;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .orb-inner {
          position: relative;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: radial-gradient(
            ellipse 90% 90% at 50% 45%,
            rgba(0, 82, 255, 0.08) 0%,
            rgba(0, 40, 120, 0.15) 40%,
            rgba(0, 20, 60, 0.2) 100%
          );
          border: 1px solid rgba(255, 255, 255, 0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow:
            0 0 40px rgba(0, 82, 255, 0.1),
            0 0 80px rgba(0, 82, 255, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            inset 0 -2px 4px rgba(0, 0, 0, 0.2);
          animation: orbBreathe 6s ease-in-out infinite;
        }
        
        @keyframes orbBreathe {
          0%, 100% {
            box-shadow:
              0 0 40px rgba(0, 82, 255, 0.1),
              0 0 80px rgba(0, 82, 255, 0.05),
              inset 0 1px 0 rgba(255, 255, 255, 0.08),
              inset 0 -2px 4px rgba(0, 0, 0, 0.2);
          }
          50% {
            box-shadow:
              0 0 50px rgba(0, 82, 255, 0.15),
              0 0 100px rgba(0, 82, 255, 0.07),
              inset 0 1px 0 rgba(255, 255, 255, 0.1),
              inset 0 -2px 4px rgba(0, 0, 0, 0.2);
          }
        }
        
        .orb-edge-glow {
          position: absolute;
          inset: -1px;
          border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            transparent 0%,
            rgba(0, 82, 255, 0.1) 25%,
            transparent 50%,
            rgba(0, 82, 255, 0.08) 75%,
            transparent 100%
          );
          animation: rotateEdge 15s linear infinite;
          opacity: 0.6;
          pointer-events: none;
        }
        
        @keyframes rotateEdge {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* ===========================================
           CONCENTRIC RINGS
           Very subtle, slow pulse
        =========================================== */
        
        .ring-animate {
          opacity: 0;
          animation: ringFadeIn 1s ease-out forwards, ringPulse 8s ease-in-out infinite;
        }
        
        .ring-outer {
          width: 260px;
          height: 260px;
          border: 1px solid rgba(0, 82, 255, 0.08);
          animation-delay: 0.3s, 0s;
        }
        
        .ring-middle {
          width: 220px;
          height: 220px;
          border: 1px dashed rgba(0, 82, 255, 0.05);
          animation-delay: 0.5s, -2s;
        }
        
        .ring-inner {
          width: 180px;
          height: 180px;
          border: 1px solid rgba(0, 82, 255, 0.1);
          animation-delay: 0.7s, -4s;
        }
        
        @keyframes ringFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes ringPulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.8;
          }
          50% { 
            transform: scale(1.02); 
            opacity: 1;
          }
        }
      `}</style>

      {/* Header - at top */}
      <div className="text-center pt-20 pb-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-[36px] font-bold tracking-[-0.02em] text-white mb-3"
        >
          Works with everything.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-[15px] text-zinc-500"
        >
          Any framework. Any LLM. Any agent.
        </motion.p>
      </div>

      {/* Constellation Area - Desktop */}
      {!isMobile && (
        <div 
          ref={containerRef}
          className="relative w-full flex items-center justify-center"
          style={{ height: '70vh', minHeight: '500px', maxHeight: '700px' }}
        >
          {/* SVG for pulse lines */}
          <svg 
            ref={svgRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1 }}
          />

          {/* AIBL CENTER with glossy orb effect */}
          <AIBLLogo centerRef={(el) => { centerRef.current = el; }} />

          {/* Framework Icons - Pure CSS animated floating */}
          {frameworks.map((fw, i) => (
            <FloatingIcon 
              key={fw.id} 
              framework={fw}
              isVisible={isVisible}
              ref={setIconRef(i)}
            />
          ))}
        </div>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <div className="flex flex-wrap justify-center gap-6 px-6 pb-20">
          <div className="w-full flex justify-center my-8">
            <img src={AIBL_LOGO} alt="AIBL" className="w-[100px] h-[100px] object-contain" />
          </div>
          {frameworks.map((fw) => (
            <div key={fw.id} className="flex flex-col items-center gap-2">
              <div 
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <img src={fw.logo} alt={fw.name} className="w-6 h-6 object-contain rounded-md" style={{ filter: 'brightness(0.9)' }} loading="lazy" />
              </div>
              <span className="text-[11px] text-white/50">{fw.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom note - in curved glass box */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex justify-center pb-16"
      >
        <div 
          className="px-8 py-4 rounded-full"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <span className="text-[15px] text-white/60 font-medium">
            + any agent via SDK
          </span>
        </div>
      </motion.div>
    </section>
  );
}
